import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  HeartPulse,
  MoonStar,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Thermometer
} from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import {
  APPLE_HEALTH_GROUP_LABELS,
  detectAppleHealthSupport,
  getAppleHealthConnection,
  getAppleHealthMetricCatalog,
  getAppleHealthMetricsByGroup,
  getAppleHealthNativeAvailability,
  requestAppleHealthPermissions,
  syncAppleHealth
} from '@/lib/apple-health'
import type { AppleHealthAuthorizationStatus, AppleHealthMetricGroup } from '@/types/health'

const GROUP_ICON_MAP: Record<AppleHealthMetricGroup, typeof MoonStar> = {
  sleep: MoonStar,
  heart: HeartPulse,
  activity: Activity,
  vitals: Thermometer,
  body: Smartphone,
  cycle: ShieldCheck,
  mindfulness: MoonStar
}

const GROUP_ORDER: AppleHealthMetricGroup[] = [
  'sleep',
  'heart',
  'activity',
  'vitals',
  'cycle',
  'body',
  'mindfulness'
]

function formatAppleHealthDate(value: string | null | undefined) {
  if (!value) {
    return 'Pas encore'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

function getConnectionStatusLabel(status?: AppleHealthAuthorizationStatus | null) {
  switch (status) {
    case 'granted':
      return 'Autorisation accordee'
    case 'partial':
      return 'Autorisation partielle'
    case 'denied':
      return 'Autorisation refusee'
    case 'revoked':
      return 'Acces revoque'
    case 'error':
      return 'Erreur de connexion'
    default:
      return 'En attente'
  }
}

export default function AppleHealthSettingsCard() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const support = useMemo(() => detectAppleHealthSupport(), [])
  const metricsByGroup = useMemo(() => getAppleHealthMetricsByGroup(), [])
  const metricCatalog = useMemo(() => getAppleHealthMetricCatalog(), [])

  const { data: connection, isLoading } = useQuery({
    queryKey: ['apple-health-connection', user?.id],
    queryFn: () => getAppleHealthConnection(user!.id),
    enabled: Boolean(user?.id)
  })

  const { data: nativeAvailability } = useQuery({
    queryKey: ['apple-health-native-availability'],
    queryFn: getAppleHealthNativeAvailability,
    enabled: Boolean(user)
  })

  const permissionMutation = useMutation({
    mutationFn: () => requestAppleHealthPermissions(user!.id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['apple-health-connection', user?.id] })
      toast.success(
        result.status === 'granted'
          ? `${result.granted_metrics.length} autorisations Apple Health accordees`
          : 'Demande Apple Health terminee'
      )
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Autorisation Apple Health impossible')
    }
  })

  const syncMutation = useMutation({
    mutationFn: () => syncAppleHealth(user!.id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['apple-health-connection', user?.id] })
      toast.success(
        result.status === 'completed' || result.status === 'partial'
          ? `${result.imported_samples} echantillons synchronises`
          : 'Synchronisation Apple Health terminee'
      )
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Synchronisation Apple Health impossible')
    }
  })

  const grantedCount = connection?.granted_metrics?.length ?? 0
  const revokedCount = connection?.revoked_metrics?.length ?? 0
  const isNativeReady = support.available && nativeAvailability?.available !== false

  return (
    <section id="apple-health" className="space-y-6">
      <div className="card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
              <Smartphone className="h-5 w-5" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-white">Apple Health et Apple Watch</h2>
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] ${
                    support.available
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
                      : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100'
                  }`}
                >
                  {support.available ? 'Pret sur iPhone' : 'Preparation en cours'}
                </span>
              </div>
              <p className="mt-2 max-w-3xl text-sm text-slate-400">{support.reason}</p>
            </div>
          </div>

          <div className="flex flex-col items-stretch gap-3">
            <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-3 text-right">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Etat actuel</p>
              <p className="mt-1 text-sm font-semibold text-white">
                {connection ? getConnectionStatusLabel(connection.authorization_status) : 'Base prete a connecter'}
              </p>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={!user || !isNativeReady || permissionMutation.isPending}
                onClick={() => permissionMutation.mutate()}
              >
                <ShieldCheck className="h-4 w-4" />
                {permissionMutation.isPending ? 'Autorisation...' : 'Autoriser Apple Health'}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!user || !isNativeReady || syncMutation.isPending}
                onClick={() => syncMutation.mutate()}
              >
                <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                {syncMutation.isPending ? 'Synchronisation...' : 'Synchroniser maintenant'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Mode actuel</p>
            <p className="mt-2 text-base font-semibold text-white">
              {support.mode === 'ios-native-ready' ? 'App iPhone native detectee' : 'Version web preparee'}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {support.mode === 'ios-native-ready'
                ? 'Les permissions HealthKit peuvent etre demandees dans le wrapper iOS.'
                : 'Le navigateur ne peut pas acceder directement a Apple Health.'}
            </p>
            {nativeAvailability?.device_name ? (
              <p className="mt-2 text-sm text-slate-500">
                Appareil: {nativeAvailability.device_name}
                {nativeAvailability.watch_name ? ` - Montre: ${nativeAvailability.watch_name}` : ''}
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Connexion</p>
            {isLoading ? (
              <p className="mt-2 text-sm text-slate-400">Verification en cours...</p>
            ) : connection ? (
              <>
                <p className="mt-2 text-base font-semibold text-white">
                  {connection.watch_name || connection.device_name || 'Appareil Apple connecte'}
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Derniere autorisation: {formatAppleHealthDate(connection.last_authorized_at)}
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Derniere synchro: {formatAppleHealthDate(connection.last_sync_at)}
                </p>
              </>
            ) : (
              <>
                <p className="mt-2 text-base font-semibold text-white">Aucune connexion active</p>
                <p className="mt-2 text-sm text-slate-400">
                  Le schema Supabase est pret a recevoir les donnees Apple Health.
                </p>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Metriques prevues</p>
            <p className="mt-2 text-base font-semibold text-white">
              {metricCatalog.length} metriques cataloguees
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {grantedCount} autorisees{revokedCount > 0 ? `, ${revokedCount} refusees ou retirees` : ''}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
          {GROUP_ORDER.map((group) => {
            const Icon = GROUP_ICON_MAP[group]
            const metrics = metricsByGroup[group]

            return (
              <div
                key={group}
                className="rounded-2xl border border-dark-border bg-dark-surface-high p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-cyan-200">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{APPLE_HEALTH_GROUP_LABELS[group]}</p>
                    <p className="text-xs text-slate-500">{metrics.length} mesures prevues</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {metrics.map((metric) => (
                    <span
                      key={metric.key}
                      className="rounded-full border border-dark-border bg-slate-950/40 px-2.5 py-1 text-[11px] text-slate-300"
                    >
                      {metric.label}
                    </span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-cyan-500/30 bg-cyan-500/5 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Prochaine etape iPhone</p>
              <p className="text-sm text-slate-300">
                Le wrapper iPhone et le bridge HealthKit sont prepares. Il reste a ouvrir le
                projet dans Xcode, activer HealthKit, puis lancer les premieres permissions sur
                l iPhone.
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="rounded-2xl border border-dark-border bg-slate-950/40 p-4 text-sm text-slate-300">
              1. Executer le SQL Apple Health dans Supabase pour creer les tables securisees.
            </div>
            <div className="rounded-2xl border border-dark-border bg-slate-950/40 p-4 text-sm text-slate-300">
              2. Ouvrir le projet iPhone Capacitor dans Xcode et verifier la capability HealthKit.
            </div>
            <div className="rounded-2xl border border-dark-border bg-slate-950/40 p-4 text-sm text-slate-300">
              3. Autoriser puis synchroniser sommeil, coeur, cycle, activite et vitals.
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
